import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Receipt, Smartphone, MessageSquare, Settings, Users, Bot, ShieldAlert, Percent, ArrowUpRight, ArrowDownLeft, MessageSquareText, SearchIcon, Server, CreditCard, Wallet, Bell, ArrowLeftRight } from "lucide-react";
import AdminStats from "@/components/admin/AdminStats";
import AdminTransactions from "@/components/admin/AdminTransactions";
import AdminDevices from "@/components/admin/AdminDevices";
import AdminSupport from "@/components/admin/AdminSupport";
import AdminWalletSettings from "@/components/admin/AdminWalletSettings";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminAIChat from "@/components/admin/AdminAIChat";
import AdminSecurityMonitor from "@/components/admin/AdminSecurityMonitor";
import AdminCommissionSettings from "@/components/admin/AdminCommissionSettings";
import AdminWithdrawals from "@/components/admin/AdminWithdrawals";
import AdminReceiveAddresses from "@/components/admin/AdminReceiveAddresses";
import AdminDeposits from "@/components/admin/AdminDeposits";
import AdminXPubSettings from "@/components/admin/AdminXPubSettings";
import AdminQuickReplies from "@/components/admin/AdminQuickReplies";
import AdminBinanceSettings from "@/components/admin/AdminBinanceSettings";
import AdminTransactionSearch from "@/components/admin/AdminTransactionSearch";
import AdminBlockchainBalances from "@/components/admin/AdminBlockchainBalances";
import AdminContaboVPS from "@/components/admin/AdminContaboVPS";
import AdminBinanceDiagnostics from "@/components/admin/AdminBinanceDiagnostics";
import AdminCardRedemptions from "@/components/admin/AdminCardRedemptions";
import AdminUserAddresses from "@/components/admin/AdminUserAddresses";
import AdminCentralWalletSend from "@/components/admin/AdminCentralWalletSend";
import AdminNotifications from "@/components/admin/AdminNotifications";
import AdminStaff from "@/components/admin/AdminStaff";
import AdminInternalTransfers from "@/components/admin/AdminInternalTransfers";


const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isStaff, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!authLoading && !user) { navigate("/auth"); return; }
    if (!authLoading && !roleLoading && !isAdmin && !isStaff) { navigate("/dashboard"); return; }
  }, [user, authLoading, isAdmin, isStaff, roleLoading, navigate]);

  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin && !isStaff) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-[600px] h-[600px] bg-crypto-purple/5 rounded-full blur-3xl" />
      </div>

      <main className="container mx-auto px-2 sm:px-4 pt-20 sm:pt-24 pb-8 sm:pb-12 relative z-10">
        <div className="mb-4 sm:mb-8">
          <h1 className="text-xl sm:text-3xl font-bold text-foreground mb-1 sm:mb-2">{isStaff && !isAdmin ? "لوحة الموظف" : t('admin.title')}</h1>
          <p className="text-xs sm:text-base text-muted-foreground">
            {isStaff && !isAdmin ? "الرد على الدعم وقراءة السحوبات والإيداعات والتحويلات" : t('admin.subtitle')}
          </p>
        </div>

        {isAdmin && (
          <>
            <div className="mb-4 sm:mb-8"><AdminStats /></div>
            <div className="mb-4 sm:mb-8"><AdminBlockchainBalances /></div>
            <div className="mb-4 sm:mb-8"><AdminCentralWalletSend /></div>
          </>
        )}

        <Tabs defaultValue={isStaff && !isAdmin ? "support" : "ai"}>
          <div className="overflow-x-auto -mx-2 px-2 mb-4 sm:mb-6">
            <TabsList className="bg-secondary inline-flex flex-nowrap sm:flex-wrap min-w-max sm:min-w-0 h-auto gap-1 p-1">
              {isAdmin && <TabsTrigger value="search" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><SearchIcon className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>بحث</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="ai" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Bot className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.aiAssistant')}</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="security" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><ShieldAlert className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.security')}</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="users" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Users className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.users')}</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="staff" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Users className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>موظفون</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="transactions" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Receipt className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.transactions')}</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="devices" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Smartphone className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.devices')}</span></TabsTrigger>}
              <TabsTrigger value="support" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.support')}</span></TabsTrigger>
              <TabsTrigger value="deposits" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><ArrowDownLeft className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.deposits')}</span></TabsTrigger>
              <TabsTrigger value="withdrawals" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.withdrawals')}</span></TabsTrigger>
              <TabsTrigger value="internal" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><ArrowLeftRight className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>تحويلات داخلية</span></TabsTrigger>
              {isAdmin && <TabsTrigger value="commission" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Percent className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.commission')}</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="quick-replies" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><MessageSquareText className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>الردود الجاهزة</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="addresses" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Wallet className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>عناوين المستخدمين</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="card-redemptions" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><CreditCard className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>بطاقات yqqy</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="vps" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Server className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>VPS</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="notifications" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Bell className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>إشعارات</span></TabsTrigger>}
              {isAdmin && <TabsTrigger value="settings" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap"><Settings className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" /><span>{t('admin.settings')}</span></TabsTrigger>}
            </TabsList>
          </div>

          {isAdmin && <TabsContent value="ai"><AdminAIChat /></TabsContent>}
          {isAdmin && <TabsContent value="search"><AdminTransactionSearch /></TabsContent>}
          {isAdmin && <TabsContent value="security"><AdminSecurityMonitor /></TabsContent>}
          {isAdmin && <TabsContent value="users"><AdminUsers /></TabsContent>}
          {isAdmin && <TabsContent value="staff"><AdminStaff /></TabsContent>}
          {isAdmin && <TabsContent value="transactions"><AdminTransactions /></TabsContent>}
          {isAdmin && <TabsContent value="devices"><AdminDevices /></TabsContent>}
          <TabsContent value="support"><AdminSupport /></TabsContent>
          {isAdmin && <TabsContent value="commission"><AdminCommissionSettings /></TabsContent>}
          <TabsContent value="deposits"><AdminDeposits /></TabsContent>
          <TabsContent value="withdrawals"><AdminWithdrawals /></TabsContent>
          <TabsContent value="internal"><AdminInternalTransfers /></TabsContent>
          {isAdmin && <TabsContent value="quick-replies"><AdminQuickReplies /></TabsContent>}
          {isAdmin && <TabsContent value="card-redemptions"><AdminCardRedemptions /></TabsContent>}
          {isAdmin && <TabsContent value="addresses"><AdminUserAddresses /></TabsContent>}
          {isAdmin && (
            <TabsContent value="vps">
              <div className="space-y-6">
                <AdminContaboVPS />
              </div>
            </TabsContent>
          )}
          {isAdmin && <TabsContent value="notifications"><AdminNotifications /></TabsContent>}
          {isAdmin && (
            <TabsContent value="settings">
              <div className="space-y-6">
                <AdminBinanceDiagnostics />
                <AdminXPubSettings />
                <AdminBinanceSettings />
                <AdminReceiveAddresses />
                <AdminWalletSettings />
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
